import { Controller } from "@hotwired/stimulus"

import { hostPage } from "@/lib/embed_host"
import { frameFetch } from "@/lib/frame_fetch"

import type { AccessAnswer } from "./embed_access_controller"

// Proves an email address with a six-digit code inside a frame (#2041). The
// server records the intent when the code is asked for, so nothing typed here
// turns a sign-in into a join. Writes carry the access answer's forgery token,
// never the cached card's, which belongs to nobody.
type Intent = "join" | "sign_in"

interface Challenge {
  id: string
  email: string
  resend_in: number
}

interface Reply {
  status: number
  body: Record<string, unknown>
}

const CODE_LENGTH = 6

const text = (value: unknown) => (typeof value === "string" ? value : "")

export default class extends Controller {
  static targets = [
    "emailStep",
    "codeStep",
    "emailField",
    "codeField",
    "sentTo",
    "joinCopy",
    "signInCopy",
    "countdown",
    "error",
    "notice",
    "recovery",
    "recoveryMessage",
    "recoveryLink",
  ]
  static values = {
    url: String,
    spaceUrl: String,
    embedUrl: String,
    unreachableMessage: String,
    resentMessage: String,
  }

  declare readonly emailStepTarget: HTMLElement
  declare readonly codeStepTarget: HTMLElement
  declare readonly emailFieldTarget: HTMLInputElement
  declare readonly codeFieldTarget: HTMLInputElement
  declare readonly sentToTarget: HTMLElement
  declare readonly joinCopyTargets: HTMLElement[]
  declare readonly signInCopyTargets: HTMLElement[]
  declare readonly countdownTarget: HTMLElement
  declare readonly errorTargets: HTMLElement[]
  declare readonly noticeTargets: HTMLElement[]
  declare readonly recoveryTarget: HTMLElement
  declare readonly recoveryMessageTarget: HTMLElement
  declare readonly recoveryLinkTarget: HTMLAnchorElement
  declare urlValue: string
  declare spaceUrlValue: string
  declare embedUrlValue: string
  declare unreachableMessageValue: string
  declare resentMessageValue: string

  #intent: Intent = "join"
  #challenge: Challenge | null = null
  #changingEmail = false
  #token = ""
  #busy = false
  #waitingForAnswer: ((answer: AccessAnswer | null) => void) | null = null
  #hostPage: string | null = null

  // Where a join came from (#2243): read once, as the card first loads.
  connect() {
    this.#hostPage = hostPage()
  }

  // data-action="embed-account:open->email-code#start"
  // Reopening the same flow keeps its code step: the code already sent is
  // still the one in the listener's inbox.
  start(event: CustomEvent<{ intent: Intent }>) {
    const intent = event.detail.intent
    this.clearMessages()
    if (this.#challenge && this.#intent === intent) return

    this.#intent = intent
    this.#challenge = null
    this.#changingEmail = false
    this.joinCopyTargets.forEach((el) => (el.hidden = intent !== "join"))
    this.signInCopyTargets.forEach((el) => (el.hidden = intent !== "sign_in"))
    this.showStep("email")
  }

  // data-action="embed-access:changed->email-code#remember"
  remember(event: CustomEvent<AccessAnswer>) {
    this.#token = event.detail.csrf_token
    this.#waitingForAnswer?.(event.detail)
    this.#waitingForAnswer = null
  }

  // data-action="embed-access:failed->email-code#unreachable"
  unreachable() {
    this.#waitingForAnswer?.(null)
    this.#waitingForAnswer = null
  }

  async request(event: Event) {
    event.preventDefault()
    const email = this.emailFieldTarget.value.trim()
    const reply =
      this.#challenge && this.#changingEmail
        ? await this.send("PATCH", `${this.urlValue}/${this.#challenge.id}`, {
            email_challenge: { email },
          })
        : await this.send("POST", this.urlValue, {
            email_challenge: {
              email,
              intent: this.#intent,
              space_url: this.spaceUrlValue,
              embed_url: this.embedUrlValue,
              host_page: this.#hostPage ?? undefined,
            },
          })
    if (!reply) return

    if (reply.status === 201) {
      this.#challenge = reply.body as unknown as Challenge
      this.#changingEmail = false
      this.sentToTarget.textContent = this.#challenge.email
      this.codeFieldTarget.value = ""
      this.showStep("code")
      this.restartCountdown(this.#challenge.resend_in)
      this.codeFieldTarget.focus()
    } else {
      this.refused(reply)
    }
  }

  // data-action="input->email-code#typed"
  // Digits only, from the keyboard, a paste or the code autofill; six submit.
  typed() {
    const digits = this.codeFieldTarget.value
      .replace(/\D/g, "")
      .slice(0, CODE_LENGTH)
    this.codeFieldTarget.value = digits
    if (digits.length === CODE_LENGTH)
      this.codeFieldTarget.form?.requestSubmit()
  }

  async verify(event: Event) {
    event.preventDefault()
    const challenge = this.#challenge
    if (!challenge) return

    const reply = await this.send(
      "POST",
      `${this.urlValue}/${challenge.id}/verifications`,
      { email_challenge: { code: this.codeFieldTarget.value } },
    )
    if (!reply) return

    if (reply.status === 200) {
      this.#challenge = null
      this.clearMessages()
      this.dispatch("verified", { detail: reply.body })
      return
    }
    this.codeFieldTarget.value = ""
    if (reply.body.state === "exhausted") {
      this.#challenge = null
      this.emailFieldTarget.value = challenge.email
      this.showStep("email")
    }
    this.refused(reply)
  }

  async resend() {
    const challenge = this.#challenge
    if (!challenge) return

    const reply = await this.send(
      "POST",
      `${this.urlValue}/${challenge.id}/resends`,
    )
    if (!reply) return

    if (reply.status === 201) {
      this.noticeTargets.forEach((el) => {
        el.textContent = this.resentMessageValue
        el.hidden = false
      })
      this.restartCountdown(Number(reply.body.resend_in))
      this.codeFieldTarget.focus()
    } else {
      this.refused(reply)
    }
  }

  changeEmail() {
    if (!this.#challenge) return
    this.#changingEmail = true
    this.emailFieldTarget.value = this.#challenge.email
    this.clearMessages()
    this.showStep("email")
    this.emailFieldTarget.focus()
  }

  private showStep(step: "email" | "code") {
    this.emailStepTarget.hidden = step !== "email"
    this.codeStepTarget.hidden = step !== "code"
    this.recoveryTarget.hidden = true
  }

  private restartCountdown(seconds: number) {
    this.countdownTarget.setAttribute(
      "data-countdown-seconds-value",
      String(seconds),
    )
  }

  private refused(reply: Reply) {
    if (reply.status === 401) {
      this.recoveryMessageTarget.textContent = text(reply.body.message)
      this.recoveryLinkTarget.href = text(reply.body.location)
      this.emailStepTarget.hidden = true
      this.codeStepTarget.hidden = true
      this.recoveryTarget.hidden = false
      return
    }
    this.showError(text(reply.body.message) || this.unreachableMessageValue)
  }

  // Each step shows its message under its own field, so the field it is about
  // is marked as well.
  private showError(message: string) {
    this.noticeTargets.forEach((el) => (el.hidden = true))
    this.errorTargets.forEach((el) => {
      el.textContent = message
      el.hidden = false
    })
    this.fields.forEach((el) => el.setAttribute("aria-invalid", "true"))
  }

  private clearMessages() {
    this.errorTargets.forEach((el) => (el.hidden = true))
    this.noticeTargets.forEach((el) => (el.hidden = true))
    this.fields.forEach((el) => el.removeAttribute("aria-invalid"))
  }

  private get fields(): HTMLInputElement[] {
    return [this.emailFieldTarget, this.codeFieldTarget]
  }

  // A session that lapsed, or a sibling frame that rotated the token, gets one
  // fresh access answer and one retry. A second refusal is the browser
  // refusing the cookie, and the listener is offered "Continue on Indee".
  private async send(
    method: string,
    path: string,
    body: unknown = {},
  ): Promise<Reply | null> {
    if (this.#busy) return null
    this.#busy = true
    this.element.setAttribute("aria-busy", "true")
    this.clearMessages()
    try {
      let reply = await this.fetchReply(method, path, body)
      if (reply && this.stale(reply)) {
        const answer = await this.freshAnswer()
        reply = answer ? await this.fetchReply(method, path, body) : null
      }
      if (!reply) this.showError(this.unreachableMessageValue)
      return reply
    } finally {
      this.#busy = false
      this.element.removeAttribute("aria-busy")
    }
  }

  private stale(reply: Reply): boolean {
    return (
      reply.status === 401 ||
      (reply.status === 422 && reply.body.state === "token_outdated")
    )
  }

  private freshAnswer(): Promise<AccessAnswer | null> {
    const answer = new Promise<AccessAnswer | null>(
      (resolve) => (this.#waitingForAnswer = resolve),
    )
    this.dispatch("stale")
    return answer
  }

  private async fetchReply(
    method: string,
    path: string,
    body: unknown,
  ): Promise<Reply | null> {
    let response: Response
    try {
      response = await frameFetch(path, {
        method,
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.#token,
        },
        body: JSON.stringify(body),
      })
    } catch {
      return null
    }
    if (response.status >= 500) return null
    let json: Record<string, unknown> = {}
    try {
      json = (await response.json()) as Record<string, unknown>
    } catch {
      // A body that is not JSON still has a status worth reading.
    }
    return { status: response.status, body: json }
  }
}
